import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Lock, User, Phone, Loader2, Bell, BellOff, Car, Check, X, Eye, EyeOff, AlertTriangle, CheckCircle2, XCircle, Search, ShieldCheck } from 'lucide-react';
import usePushNotifications from '@/hooks/usePushNotifications';
import { useTheme } from '@/context/ThemeContext';

import { auth as firebaseAuth, RecaptchaVerifier, signInWithPhoneNumber } from '@/lib/firebase';
import { detectBrowserCountry, getDialCode, COUNTRIES } from '@/lib/i18n';
import { SportMultiSelect } from '@/components/sports/SportMultiSelect';
import { CORNER_CATEGORIES, DEFAULT_CATEGORY_FOR_ROLE } from '@/lib/cornerCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Prefer ref code from current URL; otherwise fall back to one captured
  // earlier (e.g. user clicked an invite link that landed on `/`, browsed,
  // then clicked Register). LandingPage stashes ?ref= into localStorage.
  const refCode = searchParams.get('ref') || (() => {
    try { return localStorage.getItem('rollm8s_referral_code') || ''; } catch { return ''; }
  })();
  const { register } = useAuth();
  const { mode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const { isSupported, subscribe, loading: notifLoading } = usePushNotifications();
  const [isDriver, setIsDriver] = useState(null); // null = not answered, true/false = answered
  const [hasUkLicense, setHasUkLicense] = useState(null); // null = not answered, true/false = answered
  const [isEventOrganiser, setIsEventOrganiser] = useState(false);
  const [isDJ, setIsDJ] = useState(false);
  const [isMerchSeller, setIsMerchSeller] = useState(false);
  const [isJustSkater, setIsJustSkater] = useState(false);
  // Inline Corner setup — picked from the same form so the user skips
  // the legacy /setup-corner detour. Categories are only relevant when
  // the user picks a creator role (Organiser/DJ/Merch). When they untick
  // every creator role we clear the list so we don't accidentally spawn
  // a Corner for a civilian skater.
  const [cornerCategories, setCornerCategories] = useState([]);
  const showCornerSetup = isEventOrganiser || isDJ || isMerchSeller;
  const toggleCornerCategory = (id) => {
    setCornerCategories(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };
  // When a role is toggled ON, pre-select its default category so the
  // user isn't faced with an empty checklist. Toggling a role OFF leaves
  // their picks alone (they may have customised them).
  useEffect(() => {
    setCornerCategories(prev => {
      const next = new Set(prev);
      if (isEventOrganiser) next.add(DEFAULT_CATEGORY_FOR_ROLE.isEventOrganiser);
      if (isDJ) next.add(DEFAULT_CATEGORY_FOR_ROLE.isDJ);
      if (isMerchSeller) next.add(DEFAULT_CATEGORY_FOR_ROLE.isMerchSeller);
      // If the user has unticked every creator role, drop the corner.
      if (!isEventOrganiser && !isDJ && !isMerchSeller) return [];
      return Array.from(next);
    });
  }, [isEventOrganiser, isDJ, isMerchSeller]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Phase 2 — communities the new user is part of. Defaults to roller_skate
  // so they always have at least one tribe; they can add others before
  // submitting or edit later from their profile.
  const [selectedSports, setSelectedSports] = useState(['roller_skate']);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    username: '',
    phone: '',
    date_of_birth: ''
  });

  // Country selected at signup — drives:
  //   • dial-code auto-prefill on the phone field
  //   • whether we run Firebase phone OTP (UK = yes, everywhere else = no)
  //   • the default country filter on Events post-login
  // Initial value: browser locale guess.
  const [country, setCountry] = useState(() => detectBrowserCountry());
  const requiresPhoneVerification = country === 'GB';

  // Live username availability — checks the public /users/username/check-public
  // endpoint with a 350 ms debounce so we don't hammer the API on every
  // keystroke. State: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'.
  const [usernameStatus, setUsernameStatus] = useState('idle');
  const [usernameReason, setUsernameReason] = useState('');
  const [usernameSuggestion, setUsernameSuggestion] = useState('');
  useEffect(() => {
    const u = (formData.username || '').trim();
    if (!u) { setUsernameStatus('idle'); setUsernameReason(''); setUsernameSuggestion(''); return; }
    setUsernameStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/users/username/check-public`, { params: { username: u } });
        if (r.data?.available) {
          setUsernameStatus('available');
          setUsernameReason('');
          setUsernameSuggestion('');
        } else {
          setUsernameStatus(r.data?.reason === 'Already taken' ? 'taken' : 'invalid');
          setUsernameReason(r.data?.reason || 'Not available');
          setUsernameSuggestion(r.data?.suggestion || '');
        }
      } catch (e) {
        // Don't block the form on a transient check failure — the backend
        // will re-validate on submit.
        setUsernameStatus('idle');
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [formData.username]);
  const [vehicleData, setVehicleData] = useState({
    make: '',
    model: '',
    color: '',
    registration: '',
    fuel_type: 'petrol',
    engine_size: '',
    tank_fill_cost: ''
  });
  
  // DVLA Vehicle Check State
  const [vehicleCheckLoading, setVehicleCheckLoading] = useState(false);
  const [vehicleCheckResult, setVehicleCheckResult] = useState(null);
  const [vehicleCheckError, setVehicleCheckError] = useState(null);

  // Phone Verification State (Firebase Phone Auth)
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  const [showManualVerify, setShowManualVerify] = useState(false);
  const [manualVerified, setManualVerified] = useState(false);
  const [manualOtpCode, setManualOtpCode] = useState('');

  // Format phone number to E.164 format. Uses the country selected at signup
  // to derive the dial code so non-UK users aren't forced into +44.
  const formatPhoneE164 = (phone) => {
    const dial = getDialCode(country); // '+44', '+49', '+1', …
    let cleaned = (phone || '').replace(/\s/g, '').replace(/[()-]/g, '');
    // Already in E.164
    if (cleaned.startsWith('+')) return cleaned;
    // Local UK-style leading zero — convert to international format
    if (cleaned.startsWith('0')) return dial + cleaned.slice(1);
    return dial + cleaned;
  };

  // Initialize reCAPTCHA verifier
  const setupRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    recaptchaVerifierRef.current = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        toast.error('reCAPTCHA expired. Please try again.');
      }
    });
  };

  const sendOtp = async () => {
    const phone = formData.phone.trim();
    if (!phone) {
      toast.error('Please enter your phone number');
      return;
    }
    const formattedPhone = formatPhoneE164(phone);
    if (formattedPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setOtpLoading(true);
    try {
      setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(
        firebaseAuth,
        formattedPhone,
        recaptchaVerifierRef.current
      );
      setConfirmationResult(confirmation);
      setOtpSent(true);
      toast.success('Verification code sent via SMS!');
    } catch (err) {
      console.error('Firebase phone auth error:', err);
      // Clean up recaptcha on error
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      if (err.code === 'auth/invalid-phone-number') {
        toast.error('Invalid phone number format. Use format: +44 7xxx xxx xxx');
      } else if (err.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again later.');
      } else if (err.code === 'auth/quota-exceeded') {
        toast.error('SMS quota exceeded. Please try again later.');
      } else {
        toast.error(err.message || 'Failed to send verification code');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode || otpCode.length < 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setOtpLoading(true);
    try {
      // Try manual bypass code first
      try {
        const manualRes = await axios.post(`${API}/auth/verify-manual-phone-otp`, {
          phone: formData.phone.trim(),
          code: otpCode
        });
        if (manualRes.data.verified) {
          setPhoneVerified(true);
          setManualVerified(true);
          toast.success('Phone number verified!');
          return;
        }
      } catch (manualErr) {
        // Not the bypass code — continue with Firebase
      }
      if (!confirmationResult) {
        toast.error('Please request a new code first');
        setOtpLoading(false);
        return;
      }
      const result = await confirmationResult.confirm(otpCode);
      const idToken = await result.user.getIdToken();
      await axios.post(`${API}/auth/verify-firebase-phone`, {
        firebase_token: idToken,
        phone: formatPhoneE164(formData.phone.trim())
      });
      setPhoneVerified(true);
      toast.success('Phone number verified!');
      await firebaseAuth.signOut();
    } catch (error) {
      console.error('OTP verification error:', error);
      if (error.code === 'auth/invalid-verification-code') {
        toast.error('Invalid code. Please check and try again.');
      } else if (error.code === 'auth/code-expired') {
        toast.error('Code expired. Please request a new one.');
        setOtpSent(false);
        setConfirmationResult(null);
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Verification failed');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Auto-verify as soon as the user types the 6th digit — saves them from
  // staring at a "Verify" button and prevents the "phone not verified"
  // surprise at submit time. Guards make sure we only fire once per code
  // and never while an in-flight request is already running.
  useEffect(() => {
    if (otpCode.length === 6 && otpSent && !otpLoading && !phoneVerified) {
      verifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode]);

  // Debounced vehicle check function
  const verifyManualOtp = async () => {
    if (!manualOtpCode || manualOtpCode.length < 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API}/auth/verify-manual-phone-otp`, {
        phone: formData.phone.trim(),
        code: manualOtpCode
      });
      setManualVerified(true);
      setPhoneVerified(true);
      setShowManualVerify(false);
      toast.success('Phone number verified!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Debounced vehicle check function
  const checkVehicle = useCallback(async (registration) => {
    if (!registration || registration.length < 2) {
      setVehicleCheckResult(null);
      setVehicleCheckError(null);
      return;
    }
    
    // Clean registration
    const cleanReg = registration.replace(/\s/g, '').toUpperCase();
    if (cleanReg.length < 5) return; // UK plates are at least 5 chars
    
    setVehicleCheckLoading(true);
    setVehicleCheckError(null);
    
    try {
      const response = await axios.post(`${API}/vehicle/check`, {
        registration: cleanReg
      });
      
      setVehicleCheckResult(response.data);
      
      // Auto-fill vehicle details if found
      if (response.data.success && response.data.make) {
        // Map engine capacity (cc) to nearest dropdown value
        let engineSize = '';
        const cc = response.data.engine_capacity;
        if (cc) {
          const sizes = [1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.5, 3.0];
          const ccLitres = cc / 1000;
          const closest = sizes.reduce((prev, curr) => 
            Math.abs(curr - ccLitres) < Math.abs(prev - ccLitres) ? curr : prev
          );
          engineSize = closest.toFixed(1);
        }

        setVehicleData(prev => ({
          ...prev,
          make: response.data.make || prev.make,
          color: response.data.colour || prev.color,
          fuel_type: response.data.fuel_type?.toLowerCase() || prev.fuel_type,
          engine_size: engineSize || prev.engine_size
        }));
        toast.success('Vehicle details found! Fields auto-filled.');
      }
    } catch (error) {
      console.error('Vehicle check error:', error);
      setVehicleCheckError('Unable to verify vehicle');
    } finally {
      setVehicleCheckLoading(false);
    }
  }, []);

  // Check vehicle when registration changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (vehicleData.registration && isDriver) {
        checkVehicle(vehicleData.registration);
      }
    }, 800); // 800ms debounce
    
    return () => clearTimeout(timer);
  }, [vehicleData.registration, isDriver, checkVehicle]);

  // Password validation rules
  const passwordChecks = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  }, [formData.password]);

  const isPasswordValid = useMemo(() => {
    return passwordChecks.minLength && 
           passwordChecks.hasUppercase && 
           passwordChecks.hasLowercase && 
           passwordChecks.hasNumber;
  }, [passwordChecks]);

  const passwordsMatch = useMemo(() => {
    return formData.password && formData.confirmPassword && 
           formData.password === formData.confirmPassword;
  }, [formData.password, formData.confirmPassword]);

  // After register: respect ?next= if the visitor came from a deep-link
  // CTA, otherwise route to email verification. Corner setup is now done
  // inline on this very page, so we skip the legacy /setup-corner detour.
  // Open-redirect protected.
  const afterRegisterRedirect = () => {
    const next = searchParams.get('next');
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    if (safeNext) {
      navigate(safeNext);
      return;
    }
    navigate('/verify-email', { state: { email: formData.email } });
  };

  const handleEnableNotifications = async () => {
    await subscribe();
    setShowNotificationPrompt(false);
    afterRegisterRedirect();
  };

  const handleSkipNotifications = () => {
    setShowNotificationPrompt(false);
    afterRegisterRedirect();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate name is provided and contains only valid characters
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Full name is required');
      return;
    }
    if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]+$/.test(formData.name.trim())) {
      toast.error('Name can only contain letters, spaces, hyphens and apostrophes');
      return;
    }
    if (formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    // Username — must be picked here so we can skip the SetUsernameDialog
    // entirely on first login. We trust the live availability check; the
    // backend re-validates on submit and returns a 4xx if it raced.
    if (!formData.username || formData.username.trim().length < 3) {
      toast.error('Pick a username (3-20 chars).');
      return;
    }
    if (usernameStatus === 'taken' || usernameStatus === 'invalid') {
      toast.error(usernameReason || 'That username isn\u2019t available.');
      return;
    }

    // Validate email is provided
    if (!formData.email || formData.email.trim() === '') {
      toast.error('Email is required');
      return;
    }

    if (!isPasswordValid) {
      toast.error('Password does not meet requirements');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate phone number is provided
    if (!formData.phone || formData.phone.trim() === '') {
      toast.error('Phone number is required');
      return;
    }

    // Validate phone is verified — UK-only requirement. Non-UK accounts only
    // need to supply a contact number (no Firebase OTP loop).
    if (requiresPhoneVerification && !phoneVerified) {
      toast.error('Please verify your phone number first');
      return;
    }

    // Validate date of birth - must be 18+
    if (!formData.date_of_birth) {
      toast.error('Date of birth is required');
      return;
    }
    const dob = new Date(formData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age < 18) {
      toast.error('You must be 18 or over to register');
      return;
    }

    // Phase 2 — at least one community required so the personalised feed has something to show.
    if (!selectedSports || selectedSports.length === 0) {
      toast.error('Please pick at least one community to continue');
      return;
    }

    // If driver, validate vehicle fields
    if (isDriver) {
      if (!vehicleData.make || !vehicleData.model || !vehicleData.color || !vehicleData.registration) {
        toast.error('Please fill in all vehicle details');
        return;
      }
    }

    setLoading(true);

    try {
      const registerData = {
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        username: formData.username.trim().toLowerCase(),
        phone: formatPhoneE164(formData.phone.trim()),
        date_of_birth: formData.date_of_birth,
        // UK users go through Firebase OTP → phone_verified=true (or =false if
        // manualVerified). Non-UK users skip verification → store as unverified
        // but never gate them on it.
        phone_verified: requiresPhoneVerification ? !manualVerified : false,
        phone_verification_method: requiresPhoneVerification
          ? (manualVerified ? 'manual' : 'firebase_sms')
          : 'unverified_non_uk',
        role: 'member',
        country: country || detectBrowserCountry(),
        is_driver: isDriver || false,
        has_uk_license: hasUkLicense || false,
        is_event_organiser: isEventOrganiser || false,
        is_dj: isDJ || false,
        is_merch_seller: isMerchSeller || false,
        sports: selectedSports,
        // Inline Corner setup — only send if at least one category was
        // picked (avoids spawning empty Corners for civilian skaters).
        ...(cornerCategories.length > 0 ? { corner_categories: cornerCategories } : {}),
        ...(refCode ? { referral_code: refCode } : {})
      };
      
      // Add vehicle data if driver
      if (isDriver) {
        // Include DVLA verification data
        registerData.vehicle = {
          ...vehicleData,
          dvla_verified: vehicleCheckResult?.success && vehicleCheckResult?.is_tax_valid && vehicleCheckResult?.is_mot_valid,
          tax_status: vehicleCheckResult?.tax_status,
          tax_due_date: vehicleCheckResult?.tax_due_date,
          mot_status: vehicleCheckResult?.mot_status,
          mot_expiry_date: vehicleCheckResult?.mot_expiry_date,
          is_tax_valid: vehicleCheckResult?.is_tax_valid || false,
          is_mot_valid: vehicleCheckResult?.is_mot_valid || false,
          dvla_check_date: vehicleCheckResult?.success ? new Date().toISOString() : null
        };
      }
      
      await register(registerData);
      toast.success('Account created! One last step — grab the 6-digit code from your email (check spam too).');
      // Referral has now been attributed server-side — drop the stashed
      // code so a future visitor on the same device doesn't inherit it.
      try { localStorage.removeItem('rollm8s_referral_code'); } catch {}

      // If the visitor arrived here from a high-intent CTA (e.g. "Sign up
      // to message seller"), honour the ?next= and skip the corner setup
      // funnel — we don't want to interrupt buy intent with a vendor
      // onboarding wizard. They can finish corner setup later from the
      // menu. Open-redirect protected: only relative paths.
      const next = searchParams.get('next');
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
      if (safeNext) {
        navigate(safeNext);
        return;
      }

      // Show notification prompt if supported
      if (isSupported) {
        setShowNotificationPrompt(true);
      } else {
        // Corner is set up inline now — head straight to email verify.
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // Google signup removed
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <SEO
        title="Join RollM8s"
        description="Sign up for RollM8s — the UK Skate, Wheels & Boards community ride-share platform. Create your account to find skate, board and wheel sport events, get tickets, and share rides."
        path="/register"
        keywords="join rollm8s, sign up skate wheels boards UK, BMX, skateboard, roller skating registration UK"
      />
      <div className="w-full max-w-md space-y-4 animate-slide-up">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <img 
              src={mode === 'dark' ? '/rollm8s-logo-dark.png' : '/rollm8s-logo-light.png'} 
              alt="RollM8s Logo"
              className="w-[6rem] h-[6rem] md:w-[8rem] md:h-[8rem] object-contain"
            />
            <div className="flex flex-col items-start">
              <span className="font-logo text-[2.8rem] md:text-[4rem] tracking-wide leading-tight">
                <span className="text-foreground">ROLL</span>
                <span className="gradient-text">M8S</span>
              </span>
              <span className="text-[8px] md:text-[10px] font-bold uppercase leading-none block w-0 min-w-full" style={{ textAlign: 'justify', textAlignLast: 'justify' }}>
                <span className="text-foreground">THE UK'S FIRST </span>
                <span className="text-primary">SKATER </span>
                <span className="text-secondary">RIDE-SHARE</span>
              </span>
            </div>
          </Link>
          {/* Sign in link - moved here under logo */}
          <p className="mt-3 text-base text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-2xl text-primary hover:underline font-bold animate-blink-vivid">
              Sign in
            </Link>
          </p>
        </div>

        <Card className="bg-card border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">Join the Crew</CardTitle>
            <CardDescription>Create your account to get started</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    className={`pl-10 h-12 rounded-xl border-2 ${formData.name && !/^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]*$/.test(formData.name) ? 'border-red-500' : 'border-primary'}`}
                    value={formData.name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, name: val });
                    }}
                    required
                    data-testid="name-input"
                  />
                </div>
                {formData.name && !/^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]*$/.test(formData.name) && (
                  <p className="text-xs text-red-400">Letters, spaces, hyphens and apostrophes only</p>
                )}
              </div>

              {/* Username — picked here so we don't have to interrupt the
                  user with the SetUsernameDialog modal after their first
                  login. Live availability check with debounce. The @ prefix
                  is rendered as a static prefix so users learn the handle
                  convention without having to type it themselves. */}
              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">@</span>
                  <Input
                    id="username"
                    type="text"
                    placeholder="your_handle"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={20}
                    className={`pl-8 pr-10 h-12 rounded-xl border-2 ${
                      usernameStatus === 'available' ? 'border-emerald-500' :
                      (usernameStatus === 'taken' || usernameStatus === 'invalid') ? 'border-red-500' :
                      'border-primary'
                    }`}
                    value={formData.username}
                    onChange={(e) => {
                      // Strip @ if pasted and normalise to lowercase as the
                      // user types so what they see is what gets sent.
                      const raw = (e.target.value || '').replace(/^@/, '').toLowerCase();
                      setFormData({ ...formData, username: raw });
                    }}
                    required
                    data-testid="username-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    {usernameStatus === 'available' && <span className="text-emerald-400 text-sm">✓</span>}
                    {(usernameStatus === 'taken' || usernameStatus === 'invalid') && <span className="text-red-400 text-sm">✕</span>}
                  </span>
                </div>
                {usernameStatus === 'available' && (
                  <p className="text-xs text-emerald-400" data-testid="username-available">Nice — @{formData.username} is yours.</p>
                )}
                {(usernameStatus === 'taken' || usernameStatus === 'invalid') && (
                  <p className="text-xs text-red-400" data-testid="username-error">
                    {usernameReason}
                    {usernameSuggestion && (
                      <button
                        type="button"
                        className="ml-2 underline text-primary"
                        onClick={() => setFormData(f => ({ ...f, username: usernameSuggestion }))}
                        data-testid="username-suggestion-btn"
                      >
                        Use @{usernameSuggestion}
                      </button>
                    )}
                  </p>
                )}
                {usernameStatus === 'idle' && (
                  <p className="text-xs text-muted-foreground/70">
                    Shown on your posts — keep your real name private. 3-20 chars · letters, numbers, . _ -
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-xl border-2 border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select
                  value={country}
                  onValueChange={(v) => {
                    setCountry(v);
                    // Switching country invalidates any prior verification
                    setPhoneVerified(false);
                    setOtpSent(false);
                    setOtpCode('');
                    setConfirmationResult(null);
                  }}
                >
                  <SelectTrigger
                    id="country"
                    className="h-12 rounded-xl border-2 border-primary"
                    data-testid="country-select"
                  >
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>
                        {c.flag} {c.name} <span className="text-muted-foreground text-xs">({c.dialCode})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!requiresPhoneVerification && (
                  <p className="text-xs text-muted-foreground">
                    {(() => {
                      const c = COUNTRIES.find((x) => x.code === country);
                      const flag = c?.flag || '🌍';
                      const name = c?.name || country;
                      return `${flag} ${name} — no SMS verification needed. We'll save your number for contact only.`;
                    })()}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={`${getDialCode(country)} ...`}
                    className={`pl-10 ${phoneVerified ? 'pr-24' : (requiresPhoneVerification ? 'pr-20' : 'pr-3')} h-12 rounded-xl border-2 ${phoneVerified ? 'border-green-500' : 'border-primary'}`}
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      if (phoneVerified) {
                        setPhoneVerified(false);
                        setOtpSent(false);
                        setOtpCode('');
                        setConfirmationResult(null);
                      }
                    }}
                    required
                    disabled={phoneVerified}
                    data-testid="phone-input"
                  />
                  {phoneVerified ? (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-500 text-xs font-medium">
                      <ShieldCheck className="w-4 h-4" />
                      Verified
                    </span>
                  ) : requiresPhoneVerification ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary hover:text-primary/80 h-8 px-2"
                      onClick={sendOtp}
                      disabled={otpLoading || !formData.phone.trim() || otpSent}
                      data-testid="send-otp-btn"
                    >
                      {otpLoading && !otpSent ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      {otpSent ? 'Sent' : 'Send Code'}
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">Not public — used for ride coordination only</p>
                {/* OTP Input — UK only. Other countries skip Firebase OTP entirely. */}
                {requiresPhoneVerification && (otpSent || formData.phone.trim().length >= 10) && !phoneVerified && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter 6-digit code"
                      className="h-10 rounded-lg border-2 border-primary tracking-widest text-center font-mono"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      data-testid="otp-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-10 px-4 rounded-lg"
                      onClick={verifyOtp}
                      disabled={otpLoading || otpCode.length < 6}
                      data-testid="verify-otp-btn"
                    >
                      {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-10 px-2 text-xs text-muted-foreground"
                      onClick={() => {
                        setOtpSent(false);
                        setOtpCode('');
                        setConfirmationResult(null);
                      }}
                      data-testid="resend-otp-btn"
                    >
                      Resend
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {!requiresPhoneVerification
                    ? 'No SMS verification — just type your number with no leading zero.'
                    : phoneVerified
                    ? manualVerified
                      ? 'Your phone will be verified manually by our team'
                      : 'Your phone number has been verified'
                    : otpSent
                    ? 'Enter the code sent to your phone via SMS'
                    : 'We\'ll send a verification code via SMS'}
                </p>
                {/* Manual verification fallback — UK only */}
                {requiresPhoneVerification && !phoneVerified && (
                  <button
                    type="button"
                    className="text-xs text-primary/70 hover:text-primary underline mt-1 cursor-pointer"
                    onClick={() => setShowManualVerify(true)}
                    data-testid="manual-verify-link"
                  >
                    Having trouble receiving the code?
                  </button>
                )}
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth *</Label>
                <Input
                  id="dob"
                  type="date"
                  className="h-12 rounded-xl border-2 border-primary"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  required
                  data-testid="dob-input"
                />
                <p className="text-xs text-muted-foreground">You must be 18 or over to use RollM8s</p>
              </div>

              {/* Phase 2 — Communities the user is part of. Single source of truth
                  for personalised feed/event/listing filters across the app. */}
              <div className="space-y-2 pt-2" data-testid="register-communities-section">
                <Label>Which communities are you part of? *</Label>
                <p className="text-xs text-muted-foreground">
                  Pick one or more. We'll personalise events, gear and ride-shares to your tribe(s).
                </p>
                <SportMultiSelect
                  value={selectedSports}
                  onChange={setSelectedSports}
                  minSelected={1}
                />
              </div>

              {/* Do you currently hold a full U.K. license? - Asked first */}
              <div className="space-y-3 pt-2">
                <Label>Do you currently hold a full U.K. license?</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={hasUkLicense === true ? "default" : "outline"}
                    className={`flex-1 h-12 rounded-xl ${hasUkLicense === true ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setHasUkLicense(true)}
                    data-testid="has-uk-license-yes"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={hasUkLicense === false ? "default" : "outline"}
                    className={`flex-1 h-12 rounded-xl ${hasUkLicense === false ? 'bg-primary hover:bg-primary/90' : ''}`}
                    onClick={() => {
                      setHasUkLicense(false);
                      setIsDriver(false); // No license means can't drive
                    }}
                    data-testid="has-uk-license-no"
                  >
                    <X className="w-4 h-4 mr-2" />
                    No
                  </Button>
                </div>
              </div>

              {/* Do you drive? - Only show if they have a license */}
              {hasUkLicense === true && (
                <div className="space-y-3 pt-2 animate-slide-up">
                  <Label>Do you drive?</Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={isDriver === true ? "default" : "outline"}
                      className={`flex-1 h-12 rounded-xl ${isDriver === true ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      onClick={() => setIsDriver(true)}
                      data-testid="is-driver-yes"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={isDriver === false ? "default" : "outline"}
                      className={`flex-1 h-12 rounded-xl ${isDriver === false ? 'bg-primary hover:bg-primary/90' : ''}`}
                      onClick={() => setIsDriver(false)}
                      data-testid="is-driver-no"
                    >
                      <X className="w-4 h-4 mr-2" />
                      No
                    </Button>
                  </div>
                </div>
              )}

              {/* Vehicle Details - Show if driver */}
              {isDriver && (
                <div className="space-y-4 p-4 rounded-xl bg-secondary/10 border border-secondary/30 animate-slide-up">
                  <div className="flex items-center gap-2 text-secondary">
                    <Car className="w-5 h-5" />
                    <span className="font-medium">Vehicle Details</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter your registration plate and we'll look up your vehicle details automatically.
                  </p>
                  
                  {/* Registration Plate - First and prominent */}
                  <div className="space-y-1">
                    <Label htmlFor="registration" className="text-xs flex items-center gap-1">
                      Registration Plate *
                      {vehicleCheckLoading && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                    </Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="registration"
                        placeholder="e.g., AB12 CDE"
                        className={`h-12 rounded-lg pl-10 pr-10 font-mono text-lg tracking-widest uppercase border-2 ${
                          vehicleCheckResult?.success && vehicleCheckResult?.is_tax_valid && vehicleCheckResult?.is_mot_valid 
                            ? 'border-success' 
                            : vehicleCheckResult?.warning_message 
                              ? 'border-warning' 
                              : 'border-primary'
                        }`}
                        value={vehicleData.registration}
                        onChange={(e) => setVehicleData({ ...vehicleData, registration: e.target.value.toUpperCase() })}
                        data-testid="vehicle-registration"
                      />
                      {vehicleCheckResult?.success && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {vehicleCheckResult.is_tax_valid && vehicleCheckResult.is_mot_valid ? (
                            <CheckCircle2 className="w-5 h-5 text-success" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-warning" />
                          )}
                        </div>
                      )}
                    </div>
                    {!vehicleCheckResult && !vehicleCheckLoading && !vehicleCheckError && (
                      <p className="text-xs text-muted-foreground">We'll auto-fill your vehicle details from the DVLA</p>
                    )}
                  </div>

                  {/* DVLA Check Results */}
                  {vehicleCheckResult && (
                    <div className={`p-3 rounded-lg border ${
                      vehicleCheckResult.success && vehicleCheckResult.is_tax_valid && vehicleCheckResult.is_mot_valid
                        ? 'bg-success/10 border-success/30'
                        : vehicleCheckResult.warning_message
                          ? 'bg-warning/10 border-warning/30'
                          : 'bg-muted/50 border-border/50'
                    }`}>
                      <div className="flex items-start gap-2">
                        {vehicleCheckResult.success ? (
                          vehicleCheckResult.is_tax_valid && vehicleCheckResult.is_mot_valid ? (
                            <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                          )
                        ) : (
                          <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          {vehicleCheckResult.success ? (
                            <>
                              <p className="text-sm font-medium">
                                {vehicleCheckResult.make} - {vehicleCheckResult.colour}
                              </p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                                <span className={vehicleCheckResult.is_tax_valid ? 'text-success' : 'text-warning'}>
                                  Tax: {vehicleCheckResult.is_tax_valid ? '✓ Valid' : '✗ ' + (vehicleCheckResult.tax_status || 'Invalid')}
                                  {vehicleCheckResult.tax_due_date && ` (expires ${vehicleCheckResult.tax_due_date})`}
                                </span>
                                <span className={vehicleCheckResult.is_mot_valid ? 'text-success' : 'text-warning'}>
                                  MOT: {vehicleCheckResult.is_mot_valid ? '✓ Valid' : '✗ ' + (vehicleCheckResult.mot_status || 'Invalid')}
                                  {vehicleCheckResult.mot_expiry_date && ` (expires ${vehicleCheckResult.mot_expiry_date})`}
                                </span>
                              </div>
                              {vehicleCheckResult.warning_message && (
                                <p className="text-xs text-warning mt-2">
                                  {vehicleCheckResult.warning_message}. Your vehicle will show as unverified on your profile.
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {vehicleCheckResult.error || 'Unable to verify vehicle'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {vehicleCheckError && (
                    <p className="text-xs text-muted-foreground">{vehicleCheckError}</p>
                  )}

                  {/* Auto-filled fields section */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="make" className="text-xs flex items-center gap-1">
                        Make *
                        {vehicleCheckResult?.success && vehicleCheckResult?.make && (
                          <span className="text-[10px] text-success font-medium">auto-filled</span>
                        )}
                      </Label>
                      <Input
                        id="make"
                        placeholder="e.g., Ford"
                        className="h-10 rounded-lg border-2 border-primary"
                        value={vehicleData.make}
                        onChange={(e) => setVehicleData({ ...vehicleData, make: e.target.value })}
                        data-testid="vehicle-make"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="model" className="text-xs">Model *</Label>
                      <Input
                        id="model"
                        placeholder="e.g., Focus"
                        className="h-10 rounded-lg border-2 border-primary"
                        value={vehicleData.model}
                        onChange={(e) => setVehicleData({ ...vehicleData, model: e.target.value })}
                        data-testid="vehicle-model"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="color" className="text-xs flex items-center gap-1">
                      Colour *
                      {vehicleCheckResult?.success && vehicleCheckResult?.colour && (
                        <span className="text-[10px] text-success font-medium">auto-filled</span>
                      )}
                    </Label>
                    <Input
                      id="color"
                      placeholder="e.g., Blue"
                      className="h-10 rounded-lg border-2 border-primary"
                      value={vehicleData.color}
                      onChange={(e) => setVehicleData({ ...vehicleData, color: e.target.value })}
                      data-testid="vehicle-color"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="fuel_type" className="text-xs flex items-center gap-1">
                      Fuel Type
                      {vehicleCheckResult?.success && vehicleCheckResult?.fuel_type && (
                        <span className="text-[10px] text-success font-medium">auto-filled</span>
                      )}
                    </Label>
                    <Select value={vehicleData.fuel_type} onValueChange={(v) => setVehicleData({ ...vehicleData, fuel_type: v })}>
                      <SelectTrigger className="h-10 rounded-lg border-2 border-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="petrol">Petrol</SelectItem>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="engine_size" className="text-xs flex items-center gap-1">
                        Engine Size
                        {vehicleCheckResult?.success && vehicleCheckResult?.engine_capacity && (
                          <span className="text-[10px] text-success font-medium">auto-filled</span>
                        )}
                      </Label>
                      <Select value={vehicleData.engine_size} onValueChange={(v) => setVehicleData({ ...vehicleData, engine_size: v })}>
                        <SelectTrigger className="h-10 rounded-lg border-2 border-primary">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1.0">1.0L</SelectItem>
                          <SelectItem value="1.2">1.2L</SelectItem>
                          <SelectItem value="1.4">1.4L</SelectItem>
                          <SelectItem value="1.6">1.6L</SelectItem>
                          <SelectItem value="1.8">1.8L</SelectItem>
                          <SelectItem value="2.0">2.0L</SelectItem>
                          <SelectItem value="2.2">2.2L</SelectItem>
                          <SelectItem value="2.5">2.5L</SelectItem>
                          <SelectItem value="3.0">3.0L+</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">Helps calculate fuel efficiency</p>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="tank_fill_cost" className="text-xs">Tank Fill Cost (£)</Label>
                      <Input
                        id="tank_fill_cost"
                        type="number"
                        placeholder="e.g., 50"
                        className="h-10 rounded-lg border-2 border-primary"
                        value={vehicleData.tank_fill_cost}
                        onChange={(e) => setVehicleData({ ...vehicleData, tank_fill_cost: e.target.value })}
                        data-testid="vehicle-tank-cost"
                      />
                      <p className="text-xs text-muted-foreground">How much to fill your tank?</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Role Selection — multi-select */}
              {isDriver !== null && (
                <div className="space-y-3 animate-slide-up">
                  <Label className="text-base font-medium">What best describes you in the RollM8s community?</Label>
                  <p className="text-xs text-muted-foreground">
                    Pick all that apply. This helps us personalise your experience — you can always change it later.
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      onClick={() => { setIsEventOrganiser(v => !v); if (isJustSkater) setIsJustSkater(false); }}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        isEventOrganiser 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-border/50 hover:border-border'
                      }`}
                      data-testid="role-organiser"
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isEventOrganiser ? 'border-purple-500 bg-purple-500' : 'border-muted-foreground/30'
                      }`}>
                        {isEventOrganiser && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="text-sm font-medium">Event Organiser</span>
                        <p className="text-xs text-muted-foreground">I run or promote events & sessions</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsDJ(v => !v); if (isJustSkater) setIsJustSkater(false); }}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        isDJ 
                          ? 'border-cyan-500 bg-cyan-500/10' 
                          : 'border-border/50 hover:border-border'
                      }`}
                      data-testid="role-dj"
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isDJ ? 'border-cyan-500 bg-cyan-500' : 'border-muted-foreground/30'
                      }`}>
                        {isDJ && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="text-sm font-medium">SK8 DJ</span>
                        <p className="text-xs text-muted-foreground">I DJ at events & jams</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsMerchSeller(v => !v); if (isJustSkater) setIsJustSkater(false); }}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        isMerchSeller 
                          ? 'border-pink-500 bg-pink-500/10' 
                          : 'border-border/50 hover:border-border'
                      }`}
                      data-testid="role-merch"
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isMerchSeller ? 'border-pink-500 bg-pink-500' : 'border-muted-foreground/30'
                      }`}>
                        {isMerchSeller && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="text-sm font-medium">Merch / Brand</span>
                        <p className="text-xs text-muted-foreground">I sell merch or run a gear brand</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const next = !isJustSkater;
                        setIsJustSkater(next);
                        if (next) { setIsEventOrganiser(false); setIsDJ(false); setIsMerchSeller(false); }
                      }}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        isJustSkater 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border/50 hover:border-border'
                      }`}
                      data-testid="role-skater"
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isJustSkater ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                      }`}>
                        {isJustSkater && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="text-sm font-medium">Just here to roll/glide!</span>
                        <p className="text-xs text-muted-foreground">I'm here for the rides and events</p>
                      </div>
                    </button>
                  </div>

                  {/* Inline Corner setup — appears when the user picks a
                      creator role (Organiser / DJ / Merch). Removes the
                      legacy /setup-corner page detour so registration is
                      a single linear flow. */}
                  {showCornerSetup && (
                    <div className="space-y-3 mt-4 p-4 rounded-xl border-2 border-primary/40 bg-primary/5 animate-slide-up" data-testid="register-corner-setup">
                      <div>
                        <Label className="text-sm font-medium">Set up your Corner</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Your Corner is your public profile on RollFlix where the community can find you. Pick everything that applies — you can edit this later.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2" data-testid="register-corner-grid">
                        {CORNER_CATEGORIES.map(cat => {
                          const isSelected = cornerCategories.includes(cat.id);
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => toggleCornerCategory(cat.id)}
                              data-testid={`register-corner-cat-${cat.id}`}
                              className={`relative p-2.5 rounded-lg border-2 text-left transition-all ${
                                isSelected
                                  ? 'border-primary bg-primary/15 shadow-sm shadow-primary/10'
                                  : 'border-border/40 bg-card/40 hover:border-primary/30'
                              }`}
                            >
                              {isSelected && (
                                <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-primary-foreground" />
                                </div>
                              )}
                              <p className="font-medium text-xs pr-5">{cat.label}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{cat.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                      {cornerCategories.length === 0 && (
                        <p className="text-xs text-amber-400" data-testid="register-corner-warning">
                          Pick at least one category — otherwise we won't create a Corner for you.
                        </p>
                      )}
                    </div>
                  )}

                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a strong password"
                    className={`pl-10 pr-10 h-12 rounded-xl border-2 border-primary ${
                      formData.password && !isPasswordValid ? 'border-destructive focus:border-destructive' : ''
                    } ${formData.password && isPasswordValid ? 'border-success focus:border-success' : ''}`}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Requirements */}
                {formData.password && (
                  <div className="mt-2 p-3 rounded-lg bg-card/50 border border-border/30 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Password must contain:</p>
                    <div className="grid grid-cols-2 gap-1">
                      <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.minLength ? 'text-success' : 'text-muted-foreground'}`}>
                        {passwordChecks.minLength ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        8+ characters
                      </div>
                      <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.hasUppercase ? 'text-success' : 'text-muted-foreground'}`}>
                        {passwordChecks.hasUppercase ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        Uppercase letter
                      </div>
                      <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.hasLowercase ? 'text-success' : 'text-muted-foreground'}`}>
                        {passwordChecks.hasLowercase ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        Lowercase letter
                      </div>
                      <div className={`flex items-center gap-1.5 text-xs ${passwordChecks.hasNumber ? 'text-success' : 'text-muted-foreground'}`}>
                        {passwordChecks.hasNumber ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        Number
                      </div>
                    </div>
                    {passwordChecks.hasSpecial && (
                      <div className="flex items-center gap-1.5 text-xs text-success">
                        <Check className="w-3.5 h-3.5" />
                        Special character (bonus!)
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    className={`pl-10 pr-10 h-12 rounded-xl border-2 border-primary ${
                      formData.confirmPassword && !passwordsMatch ? 'border-destructive focus:border-destructive' : ''
                    } ${passwordsMatch ? 'border-success focus:border-success' : ''}`}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {formData.confirmPassword && (
                  <div className={`flex items-center gap-1.5 text-xs mt-1 ${passwordsMatch ? 'text-success' : 'text-destructive'}`}>
                    {passwordsMatch ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Passwords match
                      </>
                    ) : (
                      <>
                        <X className="w-3.5 h-3.5" />
                        Passwords do not match
                      </>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 font-bold"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>
      </div>

      {/* Notification Permission Prompt */}
      <Dialog open={showNotificationPrompt} onOpenChange={setShowNotificationPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
              <Bell className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-center font-heading text-2xl">Stay in the Loop!</DialogTitle>
            <DialogDescription className="text-center text-base">
              Enable notifications to know when drivers are heading to events you want to attend. 
              You'll also be notified when someone requests a ride you could offer!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            <Button
              onClick={handleEnableNotifications}
              className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 font-bold"
              disabled={notifLoading}
              data-testid="enable-notifications-btn"
            >
              {notifLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-5 h-5 mr-2" />
                  Enable Notifications
                </>
              )}
            </Button>
            <Button
              onClick={handleSkipNotifications}
              variant="ghost"
              className="w-full h-12 rounded-xl text-muted-foreground hover:text-foreground"
              data-testid="skip-notifications-btn"
            >
              <BellOff className="w-5 h-5 mr-2" />
              Maybe Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Manual Phone Verification Dialog */}
      <Dialog open={showManualVerify} onOpenChange={(open) => {
        if (!open) setManualOtpCode('');
        setShowManualVerify(open);
      }}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm rounded-2xl p-5">
          <DialogHeader>
            <DialogTitle className="text-lg">Can't receive SMS?</DialogTitle>
            <DialogDescription className="text-sm">
              Text the word <strong>CODE</strong> to the number below. We'll reply with your verification code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Text CODE to</p>
              <a
                href="sms:07495975469?body=CODE"
                className="text-xl sm:text-2xl font-mono font-bold text-primary tracking-wider"
                data-testid="support-phone-number"
              >
                07495 975 469
              </a>
            </div>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground text-center">Once you receive the code, enter it below:</p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit code"
                className="h-11 rounded-lg border-2 border-primary tracking-widest text-center font-mono text-base"
                value={manualOtpCode}
                onChange={(e) => setManualOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="manual-otp-input"
              />
              <Button
                onClick={verifyManualOtp}
                className="w-full h-11 rounded-xl"
                disabled={otpLoading || manualOtpCode.length < 6}
                data-testid="verify-manual-otp-btn"
              >
                {otpLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Verify Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Invisible reCAPTCHA container for Firebase Phone Auth */}
      <div id="recaptcha-container" ref={recaptchaContainerRef}></div>
    </div>
  );
};

export default RegisterPage;
